import type { ReactNode } from "react"

import {
  ArtBanners,
  Bento,
  BrandChips,
  BrandSpotlightBand,
  CategoryCover,
  CategoryGrid,
  CollectionBanners,
  CollectionCutouts,
  CtaBand,
  DropRail,
  FaqBand,
  GiftsByPrice,
  HeroCarousel,
  HeroPhoto,
  LookCard,
  PaymentPlan,
  ProductCarousel,
  PromoBanner,
  PromoCards,
  ScaleWall,
  StatWall,
  Testimonials,
  WorldTiles,
} from "@/components/marketplace"
import { SpotlightPoster } from "@/components/marketplace/commerce/spotlight-poster"
import type { BentoTile } from "@/components/marketplace/discovery/bento"
import { SectionHeader, SeeAllButton } from "@/components/marketplace/layout/section-header"
import type { FeedSection, KnownSectionType } from "@/features/feed/contract"
import { FeedRow } from "@/features/feed/FeedRow"
import { feedMoney, mapFeedProduct } from "@/features/feed/map-product"
import { resolveFeedLink } from "@/features/feed/resolve-link"
import type { FeedSectionProps, SectionRenderContext } from "@/features/feed/sections/types"
import type { FeedArtwork, FeedHeroArtwork, FeedLink } from "@/generated/api/types.gen"
import { formatMoney } from "@/lib/money"

type Props<T extends KnownSectionType> = FeedSectionProps<Extract<FeedSection, { type: T }>>

const href = (link: FeedLink | undefined | null, context: SectionRenderContext) => (link ? resolveFeedLink(link, context.countryCode, context.page) : undefined)
const photo = (image: FeedArtwork) => ({ image: image.url, imageAlt: image.alt ?? undefined, imageCrop: image })
const slides = (items: FeedHeroArtwork[], context: SectionRenderContext) =>
  items.map((slide) => ({ ...slide, ...photo(slide.image), cta: slide.cta?.label, ctaHref: href(slide.cta?.link, context), serveId: slide.serveId ?? undefined }))
const Heading = ({
  title,
  accent,
  eyebrow,
  seeAll,
  context,
  children,
}: {
  title?: string | null
  accent?: string | null
  eyebrow?: string | null
  seeAll?: FeedLink | null
  context: SectionRenderContext
  children: ReactNode
}) => (
  <div className="flex flex-col gap-400">
    {title && (
      <div className="px-gut">
        <SectionHeader title={title} accent={accent ?? undefined} eyebrow={eyebrow ?? undefined} action={seeAll ? <SeeAllButton href={href(seeAll, context)} /> : undefined} />
      </div>
    )}
    {children}
  </div>
)

export const SplitHeroFeedSection = ({ section, context }: Props<"SPLIT_HERO">) => <HeroCarousel slides={slides(section.data.slides, context)} priority={context.isLeadSection} />
export const PhotoHeroFeedSection = ({ section, context }: Props<"PHOTO_HERO">) => <HeroPhoto slides={slides(section.data.slides, context)} priority={context.isLeadSection} />
export const PhotoBannerFeedSection = ({ section, context }: Props<"PHOTO_BANNER">) => (
  <HeroPhoto slides={slides(section.data.slides, context)} priority={context.isLeadSection} compact />
)

const ProductRail = ({ section, context, compact = false }: Props<"PRODUCT_RAIL" | "COMPACT_PRODUCT_RAIL"> & { compact?: boolean }) => (
  <ProductCarousel
    title={section.data.title}
    accent={section.data.accent}
    eyebrow={section.data.eyebrow}
    seeAllHref={href(section.data.seeAll, context)}
    products={section.data.products.map((product) => mapFeedProduct(product, context.countryCode))}
    variant={compact ? "compact" : "default"}
  />
)
export const ProductRailFeedSection = (props: Props<"PRODUCT_RAIL">) => <ProductRail {...props} />
export const CompactProductRailFeedSection = (props: Props<"COMPACT_PRODUCT_RAIL">) => <ProductRail {...props} compact />
const Drop = ({ section, context, plain = false }: Props<"BLUE_PRODUCT_RAIL" | "PLAIN_PRODUCT_RAIL"> & { plain?: boolean }) => (
  <DropRail
    title={section.data.title}
    accent={section.data.accent}
    eyebrow={section.data.eyebrow}
    seeAllHref={href(section.data.seeAll, context)}
    products={section.data.products.map((product) => mapFeedProduct(product, context.countryCode))}
    variant={plain ? "bare" : "default"}
  />
)
export const BlueProductRailFeedSection = (props: Props<"BLUE_PRODUCT_RAIL">) => <Drop {...props} />
export const PlainProductRailFeedSection = (props: Props<"PLAIN_PRODUCT_RAIL">) => <Drop {...props} plain />

export const CategoryTilesFeedSection = ({ section, context }: Props<"CATEGORY_TILES">) => (
  <Heading {...section.data} context={context}>
    <CategoryGrid items={section.data.items.map((item) => ({ id: item.id, label: item.title, ...photo(item.image), href: href(item.link, context), serveId: item.serveId }))} />
  </Heading>
)
export const CategoryCutoutsFeedSection = ({ section, context }: Props<"CATEGORY_CUTOUTS">) => (
  <Heading {...section.data} context={context}>
    <CollectionCutouts items={section.data.items.map((item) => ({ ...item, ...photo(item.image), href: href(item.link, context) }))} priority={context.isLeadSection} />
  </Heading>
)
export const BrandLogosFeedSection = ({ section, context }: Props<"BRAND_LOGOS">) => (
  <Heading {...section.data} context={context}>
    <BrandChips items={section.data.items.map((item) => ({ id: item.id, label: item.title, ...photo(item.image), href: href(item.link, context), serveId: item.serveId }))} />
  </Heading>
)
export const PromoCardsFeedSection = ({ section, context }: Props<"PROMO_CARDS">) => (
  <Heading {...section.data} context={context}>
    <PromoCards items={section.data.items.map((item) => ({ ...item, ...photo(item.image), href: href(item.link, context) }))} priority={context.isLeadSection} />
  </Heading>
)
export const CategoryPhotosFeedSection = ({ section, context }: Props<"CATEGORY_PHOTOS">) => (
  <Heading {...section.data} context={context}>
    <WorldTiles items={section.data.items.map((item) => ({ ...item, ...photo(item.image), category: item.eyebrow, href: href(item.link, context) }))} />
  </Heading>
)
export const ImageBannersFeedSection = ({ section, context }: Props<"IMAGE_BANNERS">) => (
  <Heading {...section.data} context={context}>
    <ArtBanners
      items={section.data.items.map((item) => ({ id: item.id, ...photo(item.image), alt: item.image.alt ?? "", href: href(item.link, context), serveId: item.serveId }))}
      priority={context.isLeadSection}
    />
  </Heading>
)
export const CollectionBannersFeedSection = ({ section, context }: Props<"COLLECTION_BANNERS">) => (
  <CollectionBanners
    title={section.data.title ?? ""}
    accent={section.data.accent ?? undefined}
    eyebrow={section.data.eyebrow ?? undefined}
    seeAllHref={href(section.data.seeAll, context)}
    items={section.data.items.map((item) => ({ ...item, ...photo(item.image), href: href(item.link, context) }))}
  />
)
export const CountdownBannerFeedSection = ({ section, context }: Props<"COUNTDOWN_BANNER">) => (
  <div className="px-gut">
    <PromoBanner title={section.data.title} sub={section.data.sub} endsAt={section.data.endsAt} cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} />
  </div>
)
export const CustomerStoriesFeedSection = ({ section, context }: Props<"CUSTOMER_STORIES">) => (
  <Testimonials {...section.data} cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} />
)
export const BrandSpotlightFeedSection = ({ section, context }: Props<"BRAND_SPOTLIGHT">) => (
  <div className="px-gut">
    <BrandSpotlightBand
      {...section.data}
      {...photo(section.data.image)}
      cta={section.data.cta?.label}
      ctaHref={href(section.data.cta?.link, context)}
      tiles={section.data.tiles.map((product) => mapFeedProduct(product, context.countryCode))}
    />
  </div>
)
export const LookCardFeedSection = ({ section, context }: Props<"LOOK_CARD">) => (
  <div className="px-gut">
    <LookCard
      {...section.data}
      {...photo(section.data.image)}
      href={href(section.data.link, context)}
      tiles={section.data.tiles.map((product) => mapFeedProduct(product, context.countryCode))}
      priority={context.isLeadSection}
    />
  </div>
)
export const OutfitRowFeedSection = ({ section, context }: Props<"OUTFIT_ROW">) => (
  <Heading {...section.data} context={context}>
    <FeedRow count={section.data.cards.length} gridTemplateColumns={`repeat(${section.data.cards.length}, minmax(0, 1fr))`}>
      {section.data.cards.map((card, index) => (
        <div key={card.id} className="w-4/5 min-w-0 snap-start @5xl:w-auto">
          <LookCard
            {...card}
            {...photo(card.image)}
            href={href(card.link, context)}
            tiles={card.tiles.map((product) => mapFeedProduct(product, context.countryCode))}
            priority={context.isLeadSection && index === 0}
          />
        </div>
      ))}
    </FeedRow>
  </Heading>
)
export const CtaPanelFeedSection = ({ section, context }: Props<"CTA_PANEL">) => (
  <div className="px-gut">
    <CtaBand {...section.data} variant="default" cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} />
  </div>
)
export const AppPanelFeedSection = ({ section, context }: Props<"APP_PANEL">) =>
  !section.data.image.url.trim() ? null : (
    <div className="px-gut">
      <CtaBand {...section.data} {...photo(section.data.image)} variant="app" cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} />
    </div>
  )

export const ProductSpotlightFeedSection = ({ section, context }: Props<"PRODUCT_SPOTLIGHT">) => {
  const { product, image, lines, pill, ctaLabel, paymentLead, paymentTail } = section.data
  if (!product) return null
  return (
    <div data-nelo-item={product.id}>
      <SpotlightPoster
        {...photo(image)}
        lines={lines}
        eyebrow={pill}
        title={product.title}
        packshot={{ image: product.image.url, alt: product.image.alt ?? product.title }}
        price={formatMoney(feedMoney(product.price))}
        was={product.compareAtPrice ? formatMoney(feedMoney(product.compareAtPrice)) : undefined}
        quincena={paymentLead ? { lead: paymentLead, amount: formatMoney(feedMoney(product.installmentAmount)), tail: paymentTail } : undefined}
        rating={product.rating}
        reviewCount={product.reviewCount}
        cta={ctaLabel}
        ctaHref={href(product.link, context)}
        priority={context.isLeadSection}
      />
    </div>
  )
}

export const PriceShortcutsFeedSection = ({ section, context }: Props<"PRICE_SHORTCUTS">) => (
  <GiftsByPrice
    title={section.data.title}
    accent={section.data.accent}
    tiers={section.data.tiers.map((tier) => ({
      ...tier,
      href: href(tier.link, context),
      minPrice: tier.link.kind === "SEARCH" ? tier.link.query.minPrice : undefined,
      maxPrice: tier.link.kind === "SEARCH" ? tier.link.query.maxPrice : undefined,
    }))}
  />
)
export const StatWallFeedSection = ({ section, context }: Props<"STAT_WALL">) => (
  <Heading {...section.data} context={context}>
    <div className="px-gut">
      <StatWall stats={section.data.stats} />
    </div>
  </Heading>
)
export const PhotoWallFeedSection = ({ section, context }: Props<"PHOTO_WALL">) => (
  <Heading {...section.data} context={context}>
    <div className="px-gut">
      <ScaleWall {...section.data} cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} />
    </div>
  </Heading>
)
export const FaqFeedSection = ({ section, context }: Props<"FAQ">) => (
  <div className="px-gut">
    <FaqBand {...section.data} cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} />
  </div>
)
export const CategoryHeroFeedSection = ({ section, context }: Props<"CATEGORY_HERO">) => (
  <CategoryCover {...section.data} {...photo(section.data.image)} cta={section.data.cta?.label} ctaHref={href(section.data.cta?.link, context)} priority={context.isLeadSection} />
)
export const BentoGridFeedSection = ({ section, context }: Props<"BENTO_GRID">) => {
  const tiles: BentoTile[] = section.data.tiles.map((tile) => {
    const common = {
      id: tile.id,
      kind: tile.kind,
      span: tile.span === 2 || tile.span === 3 || tile.span === 4 ? tile.span : 1,
      href: href(tile.link, context),
      serveId: tile.serveId,
    } as const
    switch (tile.kind) {
      case "category":
        return { ...common, label: tile.label, eyebrow: tile.caption, ...photo(tile.image) }
      case "product":
        return { ...common, href: href(tile.link ?? tile.product?.link, context), product: tile.product ? mapFeedProduct(tile.product, context.countryCode) : undefined }
      case "promo":
        return { ...common, title: tile.title, accent: tile.accent, eyebrow: tile.eyebrow, ...photo(tile.image) }
      case "stat":
        return { ...common, big: tile.big, sub: tile.sub }
    }
  })
  return (
    <Heading {...section.data} context={context}>
      <div className="px-gut">
        <Bento tiles={tiles} />
      </div>
    </Heading>
  )
}
export const PaymentExampleFeedSection = ({ section, context }: Props<"PAYMENT_EXAMPLE">) => {
  const { receipt, plan, cta } = section.data
  return (
    <div className="px-gut">
      <PaymentPlan
        receiptImage={receipt.image.url}
        receiptImageAlt={receipt.image.alt ?? ""}
        receiptCrop={receipt.image}
        receiptTitle={receipt.title}
        receiptBody={receipt.body}
        receiptSello={receipt.sello}
        planImage={plan.image?.url}
        planCrop={plan.image}
        planImageAlt={plan.image?.alt}
        planTitle={plan.title}
        planBody={plan.body}
        cardHeading={plan.card.heading}
        cardBrand={plan.card.brand}
        productName={plan.card.productName}
        productMeta={plan.card.productMeta}
        productImage={plan.card.productImage?.url}
        productImageAlt={plan.card.productImage?.alt}
        productCrop={plan.card.productImage}
        todayLabel={plan.card.todayLabel}
        todayAmount={plan.card.todayAmount}
        installments={plan.card.installments}
        totalLabel={plan.card.totalLabel}
        totalAmount={plan.card.totalAmount}
        cta={cta?.label}
        ctaHref={href(cta?.link, context)}
      />
    </div>
  )
}
